/**
 * AVBP engagementTemplate Engine — Phase 1 · Sprint 3.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The per-tier CONFIG layer. One typed object per tier (L1A · L1B · L2A
 *  · L2B · L2C · L2D · L3A · L3B) that drives FOUR already-existing
 *  surfaces from a single swap point. Every tier's stages/tabs/gates/
 *  follow-up is reconciled (rev3, 2026-05-17) directly to its owner-
 *  locked FULLSCOPE doc + LADDER_DECISIONS_LOCKED — NOT to the mockups:
 *
 *    1. stages  → the anchor-bar / stage-rail markers; keys mirror each
 *                 tier's FULLSCOPE object-model `states:` chain
 *    2. tabs    → which Project Detail workflow tabs render + their
 *                 labels (the Sprint-2 dimmed Intake/Evaluation/Report/
 *                 Files placeholders now RESOLVE from here; a tab a tier
 *                 doesn't have is correctly ABSENT, not "broken")
 *    3. gates   → money-before-delivery payment gates (1 up-front · 2-part
 *                 · 5×20% phase-gated · 2-stage locked-amount) + the
 *                 curation/assignee delivery gate
 *    4. followUp → the per-tier follow-up schedule (Day-28 · Day-21+Day-44
 *                 · none · 6-mo credit window)
 *
 *  SOURCE OF TRUTH for tier behaviour: the owner-locked
 *  Owner's Inbox/01_Sessions/FULLSCOPE_{tier}_AVBP_v1_2026-05-16.html
 *  docs (each tier's §"Process steps", §"Charge model", §"AVBP object
 *  model" states/paymentGate) + LADDER_DECISIONS_LOCKED_2026-05-16.html
 *  (cross-cutting money/credit/international). Tier behaviour is NOT
 *  invented here and is NOT derived from the mockups — every stage/tab/
 *  gate/follow-up below traces to a cited FULLSCOPE/LADDER section.
 *
 *  This module is PURE + deterministic + dependency-light (only imports
 *  the working-calendar resolver). No Firebase, no React — so the Node
 *  native test runner exercises it directly. The Conversation/Project
 *  shell consumes it; nothing else owns tier logic.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  DEFAULT_CALENDAR,
  addBusinessHours,
  type WorkingCalendar,
} from './workingCalendar';

/* ════════════════════ Tier identity ════════════════════ */

/**
 * The 8 owner-priced CORE tiers (L1A–L3B). The "main staircase" of the
 * service ladder; each is sold as a standalone engagement.
 */
export type CoreTier =
  | 'L1A'
  | 'L1B'
  | 'L1C'
  | 'L2A'
  | 'L2B'
  | 'L2C'
  | 'L2D'
  | 'L3A'
  | 'L3B';

/**
 * The 3 ANCILLARY service tiers (spec v1.1.0 `engagementTemplates`
 * keys `siteVisit` / `surveyMapMaking` / `mapGrading`). Each is a
 * first-class AVBP object — attachable to a core tier (auto-triggered by
 * milestone) OR sold standalone. They carry their own engagement
 * template (stages/tabs/gates/followUp) exactly like a core tier so
 * report delivery can resolve a template for a standalone ancillary job.
 * Spec source: `engagementTemplates.{siteVisit,surveyMapMaking,mapGrading}`.
 */
export type AncillaryTier = 'SITE_VISIT' | 'SURVEY_MAP' | 'MAP_GRADING';

export type Tier = CoreTier | AncillaryTier;

/** The 9 core tiers, ladder order (L1C added 2026-06-23). */
export const CORE_TIERS: CoreTier[] = [
  'L1A',
  'L1B',
  'L1C',
  'L2A',
  'L2B',
  'L2C',
  'L2D',
  'L3A',
  'L3B',
];

/** The 3 ancillary tiers. */
export const ANCILLARY_TIERS: AncillaryTier[] = [
  'SITE_VISIT',
  'SURVEY_MAP',
  'MAP_GRADING',
];

/**
 * Every resolvable tier — 9 core (incl. L1C, 2026-06-23) + 3 ancillary = 12.
 * (Spec v1.1.0 `engagementTemplates` carried 11; L1C is the post-spec add.)
 */
export const ALL_TIERS: Tier[] = [...CORE_TIERS, ...ANCILLARY_TIERS];

/** Type guard — is this an ancillary (non-core) tier? */
export function isAncillaryTier(tier: Tier): tier is AncillaryTier {
  return (ANCILLARY_TIERS as Tier[]).includes(tier);
}

/* ════════════════════ Stages ════════════════════ */

export interface StageDef {
  /** Stable key (snake_case) — never shown, used for state tracking. */
  key: string;
  /** Client/staff-facing label rendered on the stage rail. */
  label: string;
}

/* ════════════════════ Tabs ════════════════════ */

/**
 * The full universe of Project Detail tabs across all tiers. A tier's
 * template lists only the tabs it actually has. `messages` is universal.
 * Tabs absent from a tier's list are correctly NOT rendered (Sprint-2's
 * dimmed-placeholder behaviour is replaced by real per-tier resolution).
 */
export type TabKey =
  | 'intake'
  | 'evaluation'
  | 'audit'
  | 'geometry'
  | 'concept'
  | 'verdict'
  | 'draft'
  | 'report'
  | 'brief'
  | 'production'
  | 'services'
  | 'interior'
  | 'remedies'
  | 'marma'
  | 'devta'
  | 'closure'
  | 'detailed'
  | 'construction'
  | 'delivery'
  // ── ancillary-tier tabs (spec v1.1.0 engagementTemplates) ──
  | 'schedule' //  SiteVisit scheduling surface
  | 'survey' //    SurveyJob data-collection / drafting surface
  | 'grading' //   GradingJob grid-overlay surface
  | 'qa' //        Rohit QA sign-off surface (Survey / Map-Grading)
  | 'messages'
  | 'files';

export interface TabDef {
  key: TabKey;
  label: string;
  /**
   * true → this tab's surface lands in a LATER sprint; render it visibly
   * dimmed/disabled (preserves the Sprint-2 "don't read as broken"
   * discipline) but it is genuinely PART of this tier. Distinct from a
   * tab simply being absent for a tier.
   */
  laterSprint?: boolean;
}

/* ════════════════════ Gates ════════════════════ */

export type GateKind = 'money' | 'curation' | 'consultation' | 'qa';

export interface MoneyGate {
  kind: 'money';
  /** Stable id (e.g. "advance", "stage1", "phase3"). */
  id: string;
  label: string;
  /**
   * Fixed amount in INR when the tier locks an exact figure (L1A/L1B/L3A
   * ₹11,000; L2B ₹31,000/₹20,000). `null` = computed (sqft/floor pricing,
   * or %-of-total) — resolved by the pricing engine (later sprint).
   */
  amountINR: number | null;
  /** Fraction of total when the gate is a %-split (L3B = 0.2 ×5). */
  fraction?: number;
  /** The stage key this gate must clear BEFORE. money-before-delivery. */
  blocksStageKey: string;
}

export interface CurationGate {
  kind: 'curation';
  /**
   * Curation = a QUALITY checkpoint before a final report is released:
   * the work passes a "ready to deliver" review. It is NOT an access
   * gate.
   *
   * ── SC-037 (owner ruling 2026-05-21, RELAXED) ──────────────────────
   * Report curation is NO LONGER gated to the project `assignee_uid`.
   * The assignee field is a SOFT responsibility tag (who is accountable)
   * — *any* staff member may generate a final report for *any* client,
   * assigned or not. There is no caller-uid assignee/admin recheck on
   * report generation. (Relaxes the earlier
   * project_curation_gating_assignee_model lock; spec scenario SC-037,
   * "approved 2026-05-21 — assignment is a responsibility, NOT a
   * restriction gate"; to be revisited later.)
   *
   * What the curation gate STILL does: it marks the stage that releases
   * a client-facing report, so the engine knows that stage is
   * delivery-guarding and routes it through the server-recheck seam
   * (`assertServerSideGate`). The server recheck verifies the
   * money/quality state of the project — it must NOT 403 by assignee.
   */
  blocksStageKey: string;
}

export interface ConsultationGate {
  kind: 'consultation';
  /** e.g. L2B Step-24 Rohit-led R1→R2 gate consultation. */
  id: string;
  label: string;
  blocksStageKey: string;
}

/**
 * QA sign-off gate — ancillary tiers only (Survey & Map Making, Map
 * Grading). Spec v1.1.0 marks the QA stage `gate: "approval"`: Rohit
 * verifies north + scale / zone alignment before the deliverable is
 * released. It is a delivery-guarding QUALITY checkpoint (like curation)
 * — not an assignee access gate.
 * Spec source: `engagementTemplates.surveyMapMaking.stages[6]` (stage_07)
 * + `engagementTemplates.mapGrading.stages[7]` (stage_08).
 */
export interface QaGate {
  kind: 'qa';
  /** Stable id (e.g. "qa_signoff"). */
  id: string;
  label: string;
  blocksStageKey: string;
}

export type Gate = MoneyGate | CurationGate | ConsultationGate | QaGate;

/* ════════════════════ Follow-up ════════════════════ */

export interface FollowUpDef {
  /** Days after delivery this touchpoint fires. */
  dayOffset: number;
  label: string;
}

/* ════════════════════ Revisions cap ════════════════════ */

/**
 * Machine-readable revisions policy — spec v1.1.0
 * `engagementTemplates.<key>.revisionsCap`. Lets the engine (and Phase 2
 * report delivery) tell whether a given revision round is included or
 * chargeable, instead of parsing a prose string.
 *
 *  - `included` — number of revision rounds bundled in the base fee.
 *  - `chargeableBeyond` — true when round N > included is billed extra.
 *  - `uncapped` — true when the tier has NO cap (L2C: iterations are
 *    informational only, never a gate).
 *  - `extraRoundChargeINR` — the per-extra-round fee when known
 *    (PROPOSED for L2A; null where the spec leaves it open).
 *  - `note` — verbatim spec `revisionsCap` string, for the UI / audit.
 *
 * A tier with no revision cycle (L1A/L1B/L2B — report tiers, not design
 * tiers) carries no `revisionsCap` field at all.
 */
export interface RevisionsCap {
  included: number;
  chargeableBeyond: boolean;
  uncapped: boolean;
  extraRoundChargeINR: number | null;
  note: string;
}

/* ════════════════════ The template ════════════════════ */

export interface EngagementTemplate {
  tier: Tier;
  /**
   * The tier's display name, sourced from spec v1.1.0
   * `engagementTemplates.<key>.name`.
   *  - Where the spec surfaces a Sanskrit Seal (L1A `name.sanskritSeal`
   *    = "The Pre-Possession Path · Vichara"; L1B "The Clarity Path ·
   *    Darshan"; L2B "The Alignment Path · Nidana") the displayName is
   *    that Seal label.
   *  - Where the spec does NOT surface a Sanskrit Seal (L2A/L2C/L2D/L3A/
   *    L3B — `name.sanskritSeal` is a tracking placeholder, OQ-006 still
   *    open) the displayName is the spec `name.modern` verbatim. No
   *    Sanskrit Seal is invented here.
   *  - Ancillary tiers carry their spec `name.modern` (the spec marks
   *    `sanskritSeal: "(ancillary — no tier Sanskrit Seal)"`).
   */
  displayName: string;
  stages: StageDef[];
  tabs: TabDef[];
  gates: Gate[];
  followUp: FollowUpDef[];
  /**
   * Whether this tier carries a Devta and/or Marma analytic surface,
   * and — when present — the stage key the surface is GATED to (it is
   * not available from engagement start; it unlocks at that stage).
   *
   * Drives "a tier that has no Devta/Marma shows no such surface"
   * (brief). Bindings reconciled to spec v1.1.0 `engagementTemplates`:
   *  - L3B: both, as before (kept).
   *  - L2C: BOTH exposed —
   *      · Marma → bound to `p2_services` (spec
   *        engagementTemplates.l2c.stages stage_04 phase
   *        "Phase2-ServicesMarma": "AV Marma-53 overlay on architect's
   *        structural + MEP…").
   *      · Devta → bound to `p4_remedy` (spec
   *        engagementTemplates.l2c.stages stage_07 phase
   *        "Phase4-RemedyList": aiHook "detailed remedy list — Vastu +
   *        Devta Yantra + paintings + artefacts…"). Spec-reconciliation
   *        2026-05-21: moved from `p3_interior` (a Sprint-3 inference
   *        with no spec anchor) to `p4_remedy` where the spec actually
   *        places Devta content. Audit punch-list item 7.
   *  - L3B: Marma → `p3_detailed` (spec engagementTemplates.l3b
   *        Phase 3); Devta → `p5_construction` (spec Phase 5 remedy
   *        list).
   *  - Everything else: absent (spec exposes no surface).
   * `*StageKey` is null when the surface is absent.
   */
  hasDevtaSurface: boolean;
  hasMarmaSurface: boolean;
  /** Stage key the Devta surface unlocks at (null = no Devta surface). */
  devtaStageKey: string | null;
  /** Stage key the Marma surface unlocks at (null = no Marma surface). */
  marmaStageKey: string | null;
  /**
   * Review-Queue auto-escalation budget, in *business* hours
   * (working-calendar resolved). Default 24 business-hr ≈ 3 working days.
   * Per-tier so long engagements can widen it later; uniform for now
   * (scope docs don't differentiate — flagged).
   */
  escalationBusinessHours: number;
  /**
   * Machine-readable revisions policy (spec `revisionsCap`). Present
   * only on tiers that HAVE a revision cycle (L2A/L2C/L2D/L3A). Absent
   * on report tiers (L1A/L1B/L2B) and ancillary tiers (no revision
   * loop). See `RevisionsCap`.
   */
  revisionsCap?: RevisionsCap;
}

const t = (key: string, label: string): StageDef => ({ key, label });
const tab = (key: TabKey, label: string, laterSprint = false): TabDef => ({
  key,
  label,
  ...(laterSprint ? { laterSprint } : {}),
});

/** Universal tabs every tier ends with (order: …, Messages, Files). */
const MSG = tab('messages', 'Messages');
const FILES = tab('files', 'Files', true);

/*
 * ─────────────────────────────────────────────────────────────────────
 *  SOURCE OF TRUTH (rev3 — 2026-05-17): the owner-locked FULLSCOPE docs
 *  + LADDER_DECISIONS_LOCKED, all dated 2026-05-16, in
 *  Owner's Inbox/01_Sessions/:
 *    · LADDER_DECISIONS_LOCKED_2026-05-16.html  (cross-cutting: money
 *      splits, credit, international, refund)
 *    · FULLSCOPE_{L1A,L1B,L2A,L2B,L2C,L2D,L3A,L3B}_AVBP_v1_2026-05-16.html
 *  Each FULLSCOPE doc's §"Process steps", §"Charge model" and
 *  §"AVBP object model" (its `states:` / `paymentGate:` lines) ARE the
 *  stages/gates/follow-up below. Stage keys mirror each doc's object-
 *  model `states:` chain so portal state-tracking is doc-faithful.
 *  The mockups are UI reference ONLY — process structure is NOT derived
 *  from them. Nothing here is invented; every entry traces to a doc §.
 * ─────────────────────────────────────────────────────────────────────
 */
const TEMPLATES: Record<Tier, EngagementTemplate> = {
  /* ── L1A · Property Selection · "Vichara" ──
   * FULLSCOPE_L1A §4 (20-step process) + §6 object-model states:
   *   draft → intake_gate → grids_charts_ready → per_property_eval →
   *   comparative → verdict → owner_review → rendered → delivered →
   *   closing_call → follow_up → (credit_window_open 6mo).
   * §6 paymentGate: ₹11,000 100% advance BEFORE state leaves draft
   *   (Money-Before-Work-Start) → blocks first real work (grids).
   * §6 curation = owner_review of draft (Rohit) gates render/delivery.
   * §4 step 20 + §1 forward-credit = 6-month next-level credit window. */
  L1A: {
    tier: 'L1A',
    // Spec v1.1.0 engagementTemplates.l1a.name.sanskritSeal.
    displayName: 'The Pre-Possession Path · Vichara',
    stages: [
      t('intake', 'Intake'),
      t('intake_gate', 'Intake gate'),
      // MERGED stage (owner sign-off 2026-07-01, mockup
      // 2026-07-01-l1a-streamlined-evaluate-mockup.html): the old separate
      // `grids_charts` (a read-only re-render) + `per_property` stages collapse
      // into ONE `evaluate` stage. Positioned where per_property sat — before
      // `astro_vastu`. The `answers` write-shape is UNCHANGED; this is a UI +
      // wiring merge only (the DB store collapse is a separate later phase).
      t('evaluate', 'Evaluate'),
      // Astro-Vastu confirm-VIEW stage (owner sign-off 2026-06-30). Sits AFTER
      // evaluate (bedrooms are MARKED there) and BEFORE comparative (astro
      // fit feeds §C/§D/§E/§F). It is a surfacing/curation stage over the
      // already-live resolveAstroFamily → astroBedroom → astroAllocation engine
      // — NOT a new gated workflow state; it invents no new astrology model.
      t('astro_vastu', 'Astro-Vastu'),
      t('comparative', 'Comparative'),
      t('verdict', 'Verdict'),
      t('owner_review', 'Owner review'),
      t('rendered', 'Report render'),
      t('delivered', 'Delivered'),
      t('closing_call', 'Closing call'),
      // Spec engagementTemplates.l1a.stages stage_20 = "Post-delivery
      // follow-up" (phase F). Distinct from the closing call (stage_19).
      t('post_delivery_followup', 'Post-delivery follow-up'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      // AD-048 — the L1A work tabs are LIT (real surfaces wired in ProjectDetail:
      // Evaluation = Grids & charts status + per-property findings + comparative;
      // Verdict = winner pick; Report = Composer & delivery). No longer laterSprint.
      tab('evaluation', 'Evaluation'),
      tab('verdict', 'Verdict'),
      tab('report', 'Report'),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'advance',
        // FULLSCOPE_L1A §2 + §6 paymentGate (LADDER §1 advance rule).
        // LABEL = the milestone description ONLY (no embedded amount). The
        // amount is rendered separately from the canonical `resolveGateAmount`
        // (agreed price wins; the template figure is just the default) — so
        // the label can never say "₹11,000" while the gate expects $40.
        label: '100% advance (before work starts)',
        amountINR: 11000,
        // Re-pointed 2026-07-01 from the retired `grids_charts` to the merged
        // `evaluate` stage (the first real work stage after the merge). The
        // Money-Before-Work-Start gate STILL blocks the first work stage; only
        // the stage key changed. canEnterStage/assertStageEntry read this key
        // generically, so every caller re-points with this one edit.
        blocksStageKey: 'evaluate',
      },
      // §4 step 16 owner-review of draft → gates render (step 17).
      { kind: 'curation', blocksStageKey: 'rendered' },
    ],
    // Spec engagementTemplates.l1a:
    //  - stage_20 "Post-delivery follow-up" → a real scheduled
    //    touchpoint (followUpSchedule: "post-delivery follow-up
    //    scheduled (stage_20)"). Models the AVBP scheduled-task that
    //    asks "which property finalised? proceed to next level?".
    //  - creditWindow.duration = "6 months from delivery" → the
    //    forward-credit window opening at delivery (dayOffset 0).
    followUp: [
      { dayOffset: 0, label: '6-month next-level credit window open' },
      { dayOffset: 7, label: 'Post-delivery follow-up — finalised property + next-level intent' },
    ],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── L1B · Quick Vastu Review · "Darshan" ──
   * FULLSCOPE_L1B §4 (20-step process) + §6 object-model states:
   *   draft → intake_gate → prep(grids+charts) → audit(5-step) →
   *   remedy_build → annotated_map → owner_review → rendered →
   *   delivered → impl_push(1–2) → impl_confirmed → (+28d) follow_up.
   * §6 paymentGate: ₹11,000 100% advance before state leaves draft
   *   → blocks prep. §4 step 15 owner-review gates render (step 17).
   * §6 followUpEngine: Day-28 paid call (guaranteed deliverable). */
  L1B: {
    tier: 'L1B',
    displayName: 'The Clarity Path · Darshan',
    stages: [
      t('intake', 'Intake'),
      t('intake_gate', 'Intake gate'),
      t('prep', 'Prep · grids+charts'),
      t('audit', 'Audit · 5-step'),
      t('remedy_build', 'Remedy build'),
      t('annotated_map', 'Annotated map'),
      t('owner_review', 'Owner review'),
      t('rendered', 'Report render'),
      t('delivered', 'Delivered'),
      t('impl_push', 'Impl push (1–2)'),
      t('impl_confirmed', 'Impl confirmed'),
      t('follow_up', 'Day-28 follow-up'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      tab('audit', 'Audit', true),
      tab('draft', 'Draft', true),
      tab('report', 'Report', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'advance',
        // FULLSCOPE_L1B §2 + §6 paymentGate.
        // LABEL = the milestone description ONLY (no embedded amount). The
        // amount is rendered separately from the canonical `resolveGateAmount`
        // (agreed price wins; the template figure is just the default) — so
        // the label can never say "₹11,000" while the gate expects $40.
        label: '100% advance (before work starts)',
        amountINR: 11000,
        blocksStageKey: 'prep',
      },
      // §4 step 15 owner-review of draft → gates render (step 17).
      { kind: 'curation', blocksStageKey: 'rendered' },
    ],
    // FULLSCOPE_L1B §4 step 20 + §6: Day-28 guaranteed paid call.
    followUp: [{ dayOffset: 28, label: 'Day-28 remedy check-in call' }],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── L1C · Plan Review · "Sameeksha" ──
   * Spec L1C_L2C_AVBP_INTEGRATION.md §1/§6.2: ₹11,000 report-only PLAN REVIEW
   * of an architect's drawing set. billing:'single-advance-100' — one 100%
   * advance collected BEFORE the report is released (money-before-delivery; L1
   * low-ticket pattern, no split). gridBuild:true (boundary trace → 16/32 from
   * the uploaded plans). architectCoordination:false (the bright line — no
   * redline, no architect contact). creditsToward L2A/L2B/L2C/L2D (the Setu
   * credit). Mirrors the L1A/L1B report-tier shape: advance gate blocks the
   * first real work; curation gates the report render. The auto-bridge L2C
   * upsell block is REPORT content (§5.3), not a gate. */
  L1C: {
    tier: 'L1C',
    displayName: 'The Review Path · Sameeksha',
    stages: [
      t('intake', 'Intake'),
      t('intake_gate', 'Intake gate'),
      t('grid_build', 'Grid build (from plans)'),
      t('plan_review', 'Plan review · 16-zone/32-entrance'),
      t('scorecard', 'Compliance scorecard + fix-list'),
      t('owner_review', 'Owner review'),
      t('rendered', 'Report render'),
      t('delivered', 'Delivered'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      tab('geometry', 'Grid', true),
      tab('verdict', 'Scorecard', true),
      tab('report', 'Report', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'advance',
        // Spec §6.2: 100% / ₹11,000 collected BEFORE the report is released.
        // LABEL carries no amount — the figure renders from resolveGateAmount
        // (agreed price wins; template figure is the default), exactly like L1A/L1B.
        label: '100% advance (before report release)',
        amountINR: 11000,
        blocksStageKey: 'grid_build',
      },
      // Owner-review of the draft verdict → gates report render.
      { kind: 'curation', blocksStageKey: 'rendered' },
    ],
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── L2A · Conceptual Vastu Layout ──
   * FULLSCOPE_L2A §4 (3-stage process) + §6 object-model states:
   *   quoted → paid_50 → intake_complete → base_drawing → concept_v1 →
   *   revision_round(1..n) → final_paid_100 → delivered → followup_15d.
   * §6 paymentGate: gate#1 50% blocks intake→base_drawing; gate#2 50%
   *   balance before final-drawing submission blocks delivered.
   * §4/§5 #15 + §6: required +15-day follow-up → L2C bridge. */
  L2A: {
    tier: 'L2A',
    // Spec v1.1.0 engagementTemplates.l2a.name.modern. The Sanskrit Seal
    // half is not surfaced in spec (OQ-006 open) → no Seal invented.
    displayName: 'Conceptual Vastu Layout',
    stages: [
      t('quoted', 'Quoted'),
      t('intake_complete', 'Intake complete'),
      t('base_drawing', 'Base drawing'),
      t('concept_v1', 'Concept design'),
      t('revision_round', 'Revisions ≤2'),
      t('delivered', 'Delivered'),
      t('followup_15d', '+15-day follow-up'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      tab('geometry', 'Geometry', true),
      tab('concept', 'Concept', true),
      tab('delivery', 'Delivery', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'stage1',
        // FULLSCOPE_L2A §2 + §6 paymentGate gate#1.
        label: '50% before engagement (blocks base drawing)',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'base_drawing',
      },
      {
        kind: 'money',
        id: 'stage2',
        // §6 paymentGate gate#2 — blocks delivered.
        label: '50% balance before final-drawing submission',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'delivered',
      },
      // §6 ledger/curation: final pack release gated to assignee+admin.
      { kind: 'curation', blocksStageKey: 'delivered' },
    ],
    // FULLSCOPE_L2A §4 Stage-3 + §5 #15: REQUIRED +15-day follow-up.
    followUp: [{ dayOffset: 15, label: '+15-day follow-up → L2C bridge' }],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
    // Spec engagementTemplates.l2a.revisionsCap: "1 concept + 2 revision
    // rounds included; 3rd+ round = ₹7,500/extra (PROPOSED)".
    revisionsCap: {
      included: 2,
      chargeableBeyond: true,
      uncapped: false,
      extraRoundChargeINR: 7500,
      note: '1 concept + 2 revision rounds included; 3rd+ round = ₹7,500/extra (PROPOSED)',
    },
  },

  /* ── L2B · Alignment Path · "Nidana" — two-report model ──
   * FULLSCOPE_L2B §4 (37-step, two-report) + §6 object-model states:
   *   quoted → paid_part1 → R1_intake → R1_eval → R1_delivered →
   *   R1_implementing → R1_followup → step24_gate → paid_part2 →
   *   R2_scoped → R2_build → R2_delivered → R2_implementing → day21 →
   *   day44 → closed.
   * §2/§6 paymentGate: Stage-1 ₹31,000 before R1 is initiated (blocks
   *   R1_eval); Step-24 Rohit-led R1→R2 gate consultation AND Stage-2
   *   ₹20,000 both block R2 (R2 not started before Step-24 + part2).
   *   §4: "R1 not delivered before Stage 1 clears; R2 not started
   *   before Stage 2 clears at the Step-24 gate." Curation gates each
   *   report assembly (R1 + R2). §4: Day-21 + Day-44 reviews. */
  L2B: {
    tier: 'L2B',
    displayName: 'The Alignment Path · Nidana',
    stages: [
      t('quoted', 'Quoted'),
      t('r1_intake', 'R1 Intake'),
      t('r1_eval', 'R1 Deep eval'),
      t('r1_delivered', 'R1 Delivered'),
      t('r1_implementing', 'R1 Implementing'),
      t('r1_followup', 'R1 Follow-up'),
      t('step24_gate', 'Step-24 R1→R2 gate'),
      t('r2_scoped', 'R2 Scoped'),
      t('r2_build', 'R2 Build'),
      t('r2_delivered', 'R2 Delivered'),
      t('r2_implementing', 'R2 Implementing'),
      t('day21', 'Day-21 review'),
      t('day44', 'Day-44 review'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      tab('geometry', 'Geometry', true),
      tab('draft', 'Draft', true),
      tab('report', 'Report', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'stage1',
        // FULLSCOPE_L2B §2/canonical §5: ₹31,000 before R1 initiated.
        label: 'Stage 1 — before R1 is initiated',
        amountINR: 31000,
        blocksStageKey: 'r1_eval',
      },
      {
        kind: 'consultation',
        id: 'step24',
        // §4 Step-24: Rohit-led R1→R2 gate; R2 does not start without it.
        label: 'Step-24 · Rohit-led R1→R2 gate consultation',
        blocksStageKey: 'r2_scoped',
      },
      {
        kind: 'money',
        id: 'stage2',
        // §2/§5: ₹20,000 before R2 (R1 stands alone if declined).
        label: 'Stage 2 — before R2 (declinable — R1 stands alone)',
        amountINR: 20000,
        blocksStageKey: 'r2_scoped',
      },
      // §4: each report released after its stage clears — curation
      // gates R1 assembly; R2 curation re-enforced before R2 delivery.
      { kind: 'curation', blocksStageKey: 'r1_delivered' },
      { kind: 'curation', blocksStageKey: 'r2_delivered' },
    ],
    // FULLSCOPE_L2B §4: Day-21 quick check-in + Day-44 review → closure.
    followUp: [
      { dayOffset: 21, label: 'Day-21 quick check-in' },
      { dayOffset: 44, label: 'Day-44 implementation review' },
    ],
    // §1/§5: Devta(45)+Marma(53) are R2 *report content*, not a gated
    // analytic canvas surface (contrast L2C/L3B). Object model exposes
    // no devta/marma surface field → absent here, as the doc dictates.
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── L2C · Vastu Coordination with Client's Architect ──
   * FULLSCOPE_L2C §5 (Phase 0–6) + §7 object-model states:
   *   draft → quoted → part1_paid → phase0_brief → phase1_concept →
   *   phase2_services_marma → part2_paid → phase3_interior →
   *   phase4_remedy_list → phase5_remedy_reviews → phase6_closure.
   * §7 paymentGate: Part-1 blocks phase0; Part-2 blocks the Interior
   *   Vastu Guidance Chart (phase3). §5: Marma overlay is in Phase 2
   *   (doc state `phase2_services_marma`) — confirms owner rev2
   *   marmaStageKey='p2_services'. §6/§5 Phase-6: consolidated final
   *   Vastu report at closure → curation gates p6_closure.
   * Owner rev2 (2026-05-17) Devta/Marma binding PRESERVED verbatim
   *   (devtaStageKey='p3_interior'); the L2C FULLSCOPE doc does not
   *   contradict it (Devta is part of the full grid set, consolidated
   *   in the Phase-4 remedy list; unlocking it just before P4 at the
   *   interior phase is consistent with §1.1/§5). */
  L2C: {
    tier: 'L2C',
    // Spec v1.1.0 engagementTemplates.l2c.name.modern. Sanskrit Seal not
    // surfaced in spec (OQ-006 open) → no Seal invented.
    displayName: "Vastu Coordination with Client's Architect",
    stages: [
      t('p0_brief', 'P0 · Entry & Design Brief'),
      t('p1_concept', 'P1 · Concept design review'),
      t('p2_services', 'P2 · Services drawings + Marma'),
      t('p3_interior', 'P3 · Interior design review'),
      t('p4_remedy', 'P4 · Stage-wise remedy list'),
      t('p5_reviews', 'P5 · Remedy-stage reviews'),
      t('p6_closure', 'P6 · Closure'),
    ],
    tabs: [
      tab('brief', 'Brief', true),
      tab('concept', 'Concept', true),
      tab('marma', 'Marma', true),
      tab('services', 'Services', true),
      tab('interior', 'Interior', true),
      tab('devta', 'Devta', true),
      tab('remedies', 'Remedies', true),
      tab('closure', 'Closure', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'part1',
        // FULLSCOPE_L2C §2/§7: Part-1 advance blocks Phase 0 start.
        label: 'Part 1 advance — blocks Phase 0',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'p0_brief',
      },
      {
        kind: 'money',
        id: 'part2',
        // §7 paymentGate: Part-2 blocks Interior Vastu Guidance Chart
        // (issued in Phase 3).
        label: 'Part 2 — blocks Interior Vastu Guidance Chart (Phase 3)',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'p3_interior',
      },
      // §5 Phase-6 / §6: consolidated final Vastu report at closure.
      { kind: 'curation', blocksStageKey: 'p6_closure' },
    ],
    followUp: [],
    // Spec-reconciliation 2026-05-21 (audit item 7). Marma → P2 (spec
    // stage_04 phase "Phase2-ServicesMarma" = Marma-53 overlay on the
    // structural set). Devta → P4 (spec stage_07 phase "Phase4-
    // RemedyList" — the Devta-Yantra content lives in the stage-wise
    // remedy list). The prior `p3_interior` Devta binding was a
    // Sprint-3 inference with NO spec anchor; corrected to where the
    // spec places Devta content.
    hasDevtaSurface: true,
    hasMarmaSurface: true,
    devtaStageKey: 'p4_remedy',
    marmaStageKey: 'p2_services',
    escalationBusinessHours: 24,
    // Spec engagementTemplates.l2c.revisionsCap: "NO CAP on design
    // iterations across all phases (LOCKED); iteration count is
    // informational only, never a gate".
    revisionsCap: {
      included: 0,
      chargeableBeyond: false,
      uncapped: true,
      extraRoundChargeINR: null,
      note: 'NO CAP on design iterations across all phases (LOCKED); iteration count is informational only, never a gate',
    },
  },

  /* ── L2D · Complete Vastu Execution Package (One-Shot) ──
   * FULLSCOPE_L2D §4 (single-handover) + §7 object-model states:
   *   draft → quoted → stage1_paid → inputs_carried (or
   *   inputs_collected) → production → stage2_paid → handover →
   *   revision(≤3) → closed | on_demand_visit (side branch).
   * §7 paymentGate: Stage-1 blocks production; Stage-2 blocks release
   *   of the consolidated package (handover). §7 singleHandover: bundle
   *   released only as ONE bundle after stage2_paid → curation gates
   *   handover. NO scheduled follow-up (one-shot; visits pay-as-needed
   *   side branch). §1/§5: full grid set incl. Devta/Marma is INTERNAL
   *   to production, never a staged client surface → no surface field. */
  L2D: {
    tier: 'L2D',
    // Spec v1.1.0 engagementTemplates.l2d.name.modern. Sanskrit Seal not
    // surfaced in spec (OQ-006 open) → no Seal invented.
    displayName: 'Complete Vastu Execution Package (One-Shot)',
    stages: [
      t('quoted', 'Quoted'),
      t('inputs_carried', 'Inputs carried'),
      t('production', 'Production'),
      t('handover', 'Single consolidated handover'),
      t('revision', 'Revision (≤3)'),
    ],
    tabs: [
      tab('intake', 'Intake / Carry'),
      tab('production', 'Production', true),
      tab('delivery', 'Delivery', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'stage1',
        // FULLSCOPE_L2D §2/§7: Stage-1 advance blocks production start.
        label: 'Stage 1 advance before production',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'production',
      },
      {
        kind: 'money',
        id: 'stage2',
        // §7 paymentGate: Stage-2 blocks release of the consolidated
        // package (atomic single-bundle handover).
        label: 'Stage 2 balance before atomic draft release',
        amountINR: null,
        fraction: 0.5,
        blocksStageKey: 'handover',
      },
      { kind: 'curation', blocksStageKey: 'handover' },
    ],
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
    // Spec engagementTemplates.l2d.revisionsCap: "2–3 revisions included
    // (2 = included, 3 = discretionary, >3 = re-quote / extra-revision
    // charge — owner to set)". `included: 2` is the firm bundled count;
    // the 3rd is discretionary and >3 is chargeable, so chargeableBeyond
    // is true. The extra-round fee is NOT set in spec → null (FLAGGED).
    revisionsCap: {
      included: 2,
      chargeableBeyond: true,
      uncapped: false,
      extraRoundChargeINR: null,
      note: '2–3 revisions included (2 = included, 3 = discretionary, >3 = re-quote / extra-revision charge — owner to set); bounded vs L2C uncapped',
    },
  },

  /* ── L3A · Vastu Design Brief — fixed-fee rulebook ──
   * FULLSCOPE_L3A §5 (process steps) + §6 object model. Fixed
   *   ₹11,000 / $120, 100% advance (money-before-delivery, before
   *   work begins) → blocks base map. §3 explicitly REMOVES Marma &
   *   Devta at L3A ("Marma flagging — removed", "Devta grid — not
   *   required") → no surfaces. §5: 7–14 day delivery, included 30-min
   *   architect Q&A call, exactly ONE post-consultation revision, then
   *   conditional "What Next → L2C" bridge (no timed post-delivery
   *   touchpoint defined in the doc → followUp empty). */
  L3A: {
    tier: 'L3A',
    // Spec v1.1.0 engagementTemplates.l3a.name.modern. Sanskrit Seal not
    // surfaced in spec (OQ-006 open) → no Seal invented.
    displayName: 'Vastu Design Brief',
    stages: [
      t('enquiry', 'Enquiry'),
      t('intake', 'Intake'),
      t('base_map', 'Base map'),
      t('grading', 'Basic map grading'),
      t('chart_work', 'Chart work'),
      t('placement', 'Entrance & placement intent'),
      t('bias_note', 'Family-chart bias note'),
      t('anchors', 'Structural anchors'),
      t('brief_compile', 'Brief compile'),
      t('delivery', 'Delivery (7–14 day)'),
      t('architect_qa', 'Architect Q&A call'),
      t('revision', '1 revision'),
      t('what_next', 'What Next → L2C'),
    ],
    tabs: [
      tab('intake', 'Intake'),
      tab('brief', 'Brief', true),
      tab('delivery', 'Delivery', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'advance',
        // FULLSCOPE_L3A §2: fixed ₹11,000 / $120, 100% advance.
        label: '100% advance',
        amountINR: 11000,
        blocksStageKey: 'base_map',
      },
      { kind: 'curation', blocksStageKey: 'delivery' },
    ],
    // FULLSCOPE_L3A §5/§6 define NO timed post-delivery follow-up; the
    // Q&A call + single revision are process steps, the L2C bridge is
    // conditional. No dayOffset touchpoint exists in the doc.
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
    // Spec engagementTemplates.l3a.revisionsCap: "ONE revision after
    // consultation with client (A15) — only one". A 2nd+ revision is not
    // priced in spec → chargeableBeyond true, extra fee null (FLAGGED).
    revisionsCap: {
      included: 1,
      chargeableBeyond: true,
      uncapped: false,
      extraRoundChargeINR: null,
      note: 'ONE revision after consultation with client (A15) — only one',
    },
  },

  /* ── L3B · Full Vastu-Based Design & Single-Point Delivery ──
   * FULLSCOPE_L3B §5 (5-phase flow) + §8 object-model states:
   *   entry → quoted → phase1 → phase2 → phase3 → phase4 → phase5 →
   *   constructionReviews → closed.
   * §3.2/§8 paymentGate: 5 INDEPENDENT phase gates, equal 20% each
   *   (LADDER §4 LOCKED), phase[n] paid before phase-n deliverable →
   *   each gate blocks its phase stage. §5 Phase 3 = "Detailed Design
   *   + Services + Marma" (Marma overlay here) → marmaStageKey
   *   'p3_detailed'. §5 Phase 5 = "Remedy + Construction + Closure"
   *   (stage-wise Devta/Yantra remedy list here) → devtaStageKey
   *   'p5_construction'. §5/§8: consolidated final Vastu report
   *   compiled at closure (Phase 5) → curation gates p5_construction
   *   (corrected from prior p3_detailed). No timed follow-up
   *   (construction reviews are scheduled site visits inside P5). */
  L3B: {
    tier: 'L3B',
    // Spec v1.1.0 engagementTemplates.l3b.name.modern. Sanskrit Seal not
    // surfaced in spec (OQ-006 open) → no Seal invented.
    displayName: 'Full Vastu-Based Design & Single-Point Delivery',
    stages: [
      t('p1_brief', 'P1 · Design Brief'),
      t('p2_concept', 'P2 · Conceptual Design'),
      t('p3_detailed', 'P3 · Detailed + Services + Marma'),
      t('p4_interior', 'P4 · Interior Astro-Vastu'),
      t('p5_construction', 'P5 · Remedy + Construction + Closure'),
    ],
    tabs: [
      tab('brief', 'Brief', true),
      tab('concept', 'Concept', true),
      tab('detailed', 'Detailed', true),
      tab('interior', 'Interior', true),
      tab('construction', 'Construction', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'phase1',
        // FULLSCOPE_L3B §3.2 + LADDER §4: equal 20% × 5, each before
        // that phase's deliverable.
        label: 'Phase 1 · 20%',
        amountINR: null,
        fraction: 0.2,
        blocksStageKey: 'p1_brief',
      },
      {
        kind: 'money',
        id: 'phase2',
        label: 'Phase 2 · 20%',
        amountINR: null,
        fraction: 0.2,
        blocksStageKey: 'p2_concept',
      },
      {
        kind: 'money',
        id: 'phase3',
        label: 'Phase 3 · 20%',
        amountINR: null,
        fraction: 0.2,
        blocksStageKey: 'p3_detailed',
      },
      {
        kind: 'money',
        id: 'phase4',
        label: 'Phase 4 · 20%',
        amountINR: null,
        fraction: 0.2,
        blocksStageKey: 'p4_interior',
      },
      {
        kind: 'money',
        id: 'phase5',
        label: 'Phase 5 · 20%',
        amountINR: null,
        fraction: 0.2,
        blocksStageKey: 'p5_construction',
      },
      // FULLSCOPE_L3B §5/§8: consolidated final Vastu report compiled
      // at closure (Phase 5) → curation gates p5_construction.
      { kind: 'curation', blocksStageKey: 'p5_construction' },
    ],
    followUp: [],
    // FULLSCOPE_L3B §5: Marma overlay is in Phase 3 ("Detailed Design
    // + Services + Marma"); stage-wise Devta/Yantra remedy list is in
    // Phase 5 ("Remedy + Construction + Closure").
    hasDevtaSurface: true,
    hasMarmaSurface: true,
    devtaStageKey: 'p5_construction',
    marmaStageKey: 'p3_detailed',
    escalationBusinessHours: 24,
  },

  /* ══════════════════ ANCILLARY TIERS ══════════════════
   * Spec v1.1.0 engagementTemplates {siteVisit, surveyMapMaking,
   * mapGrading}. Each is a first-class AVBP object — attachable to a
   * core tier (auto-triggered by milestone) OR sold standalone. They
   * carry a full engagement template so a STANDALONE ancillary job has
   * a template to resolve (without this, report delivery for a
   * standalone Site Visit / Survey / Map Grading job would fail —
   * audit punch-list blocker 1). Stage keys mirror each spec entry's
   * `stages[].id` chain; gates trace to the spec `moneyGates` + each
   * stage's `gate` field. Source doc cross-check:
   * FULLSCOPE_ANCILLARY_AVBP_v1_2026-05-16.html §1/§2/§3. */

  /* ── Site Visit · ANCILLARY-SV ──
   * Spec engagementTemplates.siteVisit: 8 stages (stage_01..stage_08).
   * Money gate at stage_03 ("Client approves + pays" — fee + outstation
   * travel advance BEFORE scheduling). Delivery at stage_08 ("Site
   * Visit Report issued"). No QA stage, no timed follow-up — the
   * engagement ends at delivery + ledger write (§1.3/§1.4). Per-visit
   * rupee amounts are mode×location×free-tier computed by lib/ancillary
   * (quoteSiteVisit) — the template carries amountINR:null, exactly as
   * the area-priced core tiers do. */
  SITE_VISIT: {
    tier: 'SITE_VISIT',
    // Spec engagementTemplates.siteVisit.name.modern (sanskritSeal =
    // "(ancillary — no tier Sanskrit Seal)").
    displayName: 'Site Visit',
    stages: [
      t('visit_requested', 'Visit request raised'),
      t('entitlement_check', 'Entitlement check + quote'),
      t('paid', 'Client approves + pays'),
      t('scheduled', 'Scheduling — date & attendee'),
      t('pre_visit_checklist', 'Pre-visit checklist'),
      t('visit_conducted', 'Visit conducted'),
      t('ledger_written', 'Observations → Project Ledger'),
      t('report_issued', 'Site Visit Report issued'),
    ],
    tabs: [
      tab('intake', 'Request'),
      tab('schedule', 'Schedule', true),
      tab('report', 'Visit Report', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'visit_fee',
        // Spec siteVisit.moneyGates[0] atStage stage_03: fee +
        // outstation travel advance BEFORE scheduling. Amount is
        // mode×location×free-tier computed (lib/ancillary).
        label: 'Visit fee + outstation travel advance (before scheduling)',
        amountINR: null,
        blocksStageKey: 'scheduled',
      },
    ],
    // Spec siteVisit: no QA stage, no timed post-delivery follow-up —
    // the SiteVisit object ends at report delivery (§1.3 step 8).
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── Survey & Map Making · ANCILLARY-SMM ──
   * Spec engagementTemplates.surveyMapMaking: 8 stages (stage_01..
   * stage_08). Money gate at stage_02 ("Quote → approve + pay" — fee
   * BEFORE work begins). QA gate at stage_07 ("Rohit verifies north +
   * scale", spec gate:"approval"). Delivery at stage_08. Per-scenario
   * pricing (A/B/C) is computed by lib/ancillary. No timed follow-up. */
  SURVEY_MAP: {
    tier: 'SURVEY_MAP',
    // Spec engagementTemplates.surveyMapMaking.name.modern.
    displayName: 'Survey & Map Making',
    stages: [
      t('request_classified', 'Request raised — scenario A/B/C'),
      t('paid', 'Quote → approve + pay'),
      t('data_collection', 'Scenario data collection'),
      t('autocad_draft', 'AutoCAD draft to scale'),
      t('north_applied', 'North applied + verified'),
      t('grid_overlays', 'Grid overlays (if ordered)'),
      t('qa', 'QA — Rohit verifies north + scale'),
      t('delivered', 'Deliver base map + grids'),
    ],
    tabs: [
      tab('intake', 'Request'),
      tab('survey', 'Survey', true),
      tab('grading', 'Grids', true),
      tab('qa', 'QA', true),
      tab('delivery', 'Delivery', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'survey_fee',
        // Spec surveyMapMaking.moneyGates[0] atStage stage_02: fee
        // BEFORE work begins (scenario-priced — lib/ancillary).
        label: 'Survey/draft fee (before work begins)',
        amountINR: null,
        blocksStageKey: 'data_collection',
      },
      {
        kind: 'qa',
        id: 'qa_signoff',
        // Spec surveyMapMaking.stages stage_07 gate:"approval" — Rohit
        // verifies north + scale before the base map is delivered.
        label: 'Rohit QA sign-off — north + scale verified',
        blocksStageKey: 'delivered',
      },
    ],
    // Spec surveyMapMaking: no timed post-delivery follow-up — the
    // SurveyJob ends at base-map delivery (§2.3 step 8).
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },

  /* ── Map Grading (Grid Making) · ANCILLARY-MG ──
   * Spec engagementTemplates.mapGrading: 9 stages (stage_01..stage_09).
   * stage_01 carries a gradeability gate (spec gate:"approval") — a map
   * must pass scale+north+dimensions before grading proceeds; a fail
   * auto-routes to Survey Scenario-C (stage_02, conditional). Money gate
   * at stage_03 ("Quote → approve + pay"). QA gate at stage_08 ("Rohit
   * verifies north + zone alignment"). Delivery at stage_09. Per-floor
   * + per-chart pricing computed by lib/ancillary. No timed follow-up. */
  MAP_GRADING: {
    tier: 'MAP_GRADING',
    // Spec engagementTemplates.mapGrading.name.modern.
    displayName: 'Map Grading (Grid Making)',
    stages: [
      t('gradeability_check', 'Request + gradeability validator'),
      t('survey_reroute', 'Auto-route to Survey-C (if ungradeable)'),
      t('paid', 'Quote → approve + pay'),
      t('map_imported', 'Map imported to grid engine'),
      t('north_set', 'North set + Rohit-verified'),
      t('overlays_generated', 'Generate ordered overlays'),
      t('astro_charts', 'Astro charts (if ordered)'),
      t('qa', 'QA — Rohit verifies north + zones'),
      t('delivered', 'Deliver graded map set'),
    ],
    tabs: [
      tab('intake', 'Request'),
      tab('grading', 'Grading', true),
      tab('qa', 'QA', true),
      tab('delivery', 'Delivery', true),
      MSG,
      FILES,
    ],
    gates: [
      {
        kind: 'money',
        id: 'grading_fee',
        // Spec mapGrading.moneyGates[0] atStage stage_03: fee BEFORE
        // work begins (per-floor + per-chart — lib/ancillary).
        label: 'Grading fee (before work begins)',
        amountINR: null,
        blocksStageKey: 'map_imported',
      },
      {
        kind: 'qa',
        id: 'qa_signoff',
        // Spec mapGrading.stages stage_08 gate:"approval" — Rohit
        // verifies north + zone alignment before the graded pack ships.
        label: 'Rohit QA sign-off — north + zone alignment verified',
        blocksStageKey: 'delivered',
      },
    ],
    // Spec mapGrading stage_01 also carries a gradeability "approval"
    // gate, but it is a VALIDATOR that conditionally routes to Survey-C
    // (stage_02) rather than a clearable gate that blocks a later
    // stage. It is modelled in lib/ancillary's gradeabilityValidator,
    // not as an engagement-template gate. (FLAGGED in the dispatch
    // report — if the owner wants it as a first-class template gate it
    // is a small follow-up.)
    followUp: [],
    hasDevtaSurface: false,
    hasMarmaSurface: false,
    devtaStageKey: null,
    marmaStageKey: null,
    escalationBusinessHours: 24,
  },
};

/* ════════════════════ Public resolver API ════════════════════ */

/**
 * Maps every spelling of an ANCILLARY tier — spec key (`siteVisit`),
 * spec tierCode (`ANCILLARY-SV`), and the engine's own `Tier` value
 * (`SITE_VISIT`) — onto its canonical `AncillaryTier`. All keys are
 * upper-cased + alphanumerics-only so a loose label still matches.
 */
const ANCILLARY_ALIASES: Record<string, AncillaryTier> = {
  // Site Visit
  SITEVISIT: 'SITE_VISIT',
  ANCILLARYSV: 'SITE_VISIT',
  // Survey & Map Making
  SURVEYMAPMAKING: 'SURVEY_MAP',
  SURVEYMAP: 'SURVEY_MAP',
  ANCILLARYSMM: 'SURVEY_MAP',
  // Map Grading
  MAPGRADING: 'MAP_GRADING',
  ANCILLARYMG: 'MAP_GRADING',
};

/**
 * Normalise a loose tier string → Tier.
 *  - Core: matches an `L[123][ABCD]` code anywhere in the string
 *    (e.g. "L2B · Detailed Assessment" → L2B).
 *  - Ancillary: matches the spec key / tierCode / engine value
 *    (e.g. "siteVisit", "ANCILLARY-SV", "SITE_VISIT" → SITE_VISIT).
 * The old regex-only form could not match the 3 ancillary tiers at all
 * (audit punch-list blocker 1) — this is the fix.
 */
export function parseTier(raw: string | undefined | null): Tier | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  // Core tier — an L-code anywhere in the string.
  const m = upper.match(/L[123][ABCD]/);
  const code = m?.[0] as Tier | undefined;
  if (code && code in TEMPLATES) return code;
  // Ancillary tier — alias lookup on the alphanumeric-stripped string.
  const stripped = upper.replace(/[^A-Z0-9]/g, '');
  const ancillary = ANCILLARY_ALIASES[stripped];
  if (ancillary && ancillary in TEMPLATES) return ancillary;
  return null;
}

/**
 * THE single source-of-truth lookup. Everything per-tier flows through
 * here. Unknown tier → null (caller decides fallback; the Conversation
 * shell falls back to L2B, the Sprint-2 reference tier, and says so).
 */
export function getEngagementTemplate(
  tier: Tier | string | null | undefined,
): EngagementTemplate | null {
  if (!tier) return null;
  const code = (typeof tier === 'string' ? parseTier(tier) : tier) as Tier | null;
  return code ? TEMPLATES[code] : null;
}

/** Stage rail rows for the shell. Marks the active stage `now`. */
export function stageRailFor(
  tpl: EngagementTemplate,
  activeStageKey: string,
): Array<{ label: string; state: 'done' | 'now' | 'todo' }> {
  const idx = tpl.stages.findIndex((s) => s.key === activeStageKey);
  return tpl.stages.map((s, i) => ({
    label: s.label,
    state: idx < 0 ? 'todo' : i < idx ? 'done' : i === idx ? 'now' : 'todo',
  }));
}

/**
 * Tabs to render for a tier. Tabs not in the tier's template simply do
 * not appear (correctly absent — NOT broken). `laterSprint:true` tabs
 * appear but disabled/dimmed (Sprint-2 discipline preserved).
 */
export function tabsFor(tpl: EngagementTemplate): TabDef[] {
  return tpl.tabs;
}

/** Is a given tab part of this tier at all? */
export function tierHasTab(tpl: EngagementTemplate, key: TabKey): boolean {
  return tpl.tabs.some((x) => x.key === key);
}

/* ── Devta / Marma surface gating ── */

export type AnalyticSurface = 'devta' | 'marma';

/**
 * The stage key a given analytic surface unlocks at, or null when the
 * tier doesn't carry it. (L2C Marma→`p2_services`, Devta→`p3_interior`
 * — owner rev2; L3B Marma→`p3_detailed`, Devta→`p5_construction` —
 * FULLSCOPE_L3B §5.)
 */
export function surfaceStageKey(
  tpl: EngagementTemplate,
  surface: AnalyticSurface,
): string | null {
  if (surface === 'devta') return tpl.hasDevtaSurface ? tpl.devtaStageKey : null;
  return tpl.hasMarmaSurface ? tpl.marmaStageKey : null;
}

/**
 * Is the Devta/Marma surface available yet, given the active stage? It
 * is GATED — absent before its bound stage, unlocked from that stage
 * onward. A tier without the surface always returns false.
 */
export function isSurfaceUnlocked(
  tpl: EngagementTemplate,
  surface: AnalyticSurface,
  activeStageKey: string,
): boolean {
  const gateKey = surfaceStageKey(tpl, surface);
  if (!gateKey) return false;
  const gateIdx = tpl.stages.findIndex((s) => s.key === gateKey);
  const activeIdx = tpl.stages.findIndex((s) => s.key === activeStageKey);
  if (gateIdx < 0 || activeIdx < 0) return false;
  return activeIdx >= gateIdx;
}

/* ── Gate evaluation ── */

export interface GateState {
  /** money gate id → paid? */
  moneyPaid: Record<string, boolean>;
  /**
   * curation quality-checkpoint cleared? (SC-037: a quality gate, NOT an
   * assignee access gate — see `CurationGate`).
   */
  curationCleared: boolean;
  consultationDone: Record<string, boolean>;
  /**
   * qa gate id → Rohit sign-off recorded? (ancillary tiers only).
   * OPTIONAL so every existing core-tier caller / persisted doc that
   * predates the ancillary tiers stays byte-for-byte valid — a missing
   * map is treated as "no QA gate cleared".
   */
  qaSignedOff?: Record<string, boolean>;
}

/**
 * Can the engagement legally ENTER `stageKey`? Enforces every gate that
 * `blocksStageKey === stageKey`.
 *
 * money-before-delivery (locked memory project_money_before_delivery) +
 * curation/assignee gating (locked project_curation_gating_assignee_model)
 * are enforced here, client-side, for UX.
 *
 * ── SERVER-RECHECK SEAM ──────────────────────────────────────────────
 * Wherever a gate guards actual DELIVERY, the authoritative re-check
 * MUST run server-side (Cloud Function / Firestore rule) — a client can
 * lie. That server enforcement is a later sprint, mirroring how Sprint 2
 * left `applyProposalToRecord` an explicit typed no-op seam. The seam is
 * `assertServerSideGate()` below — single, marked insertion point.
 */
/**
 * The payment-mode policy literal threaded into the gate (kept as a bare
 * string union here so this pure module never imports the Firestore-backed
 * `paymentMode.ts` — no cycle, stays unit-loadable). 'payment_before' is
 * the DEFAULT so every existing caller is byte-for-byte unchanged.
 *
 * owner directive 2026-05-19: money-before-delivery is now a configurable
 * policy (see paymentMode.ts). 'work_first' → MONEY gates do not block
 * (still recorded/shown by the caller); curation + consultation gates
 * ALWAYS block (work-first relaxes money only, never review/quality).
 */
export type PaymentModePolicy = 'payment_before' | 'work_first';

export function canEnterStage(
  tpl: EngagementTemplate,
  stageKey: string,
  state: GateState,
  paymentMode: PaymentModePolicy = 'payment_before',
): { allowed: boolean; blockedBy: Gate[] } {
  const blockers = tpl.gates.filter((g) => g.blocksStageKey === stageKey);
  const failed = blockers.filter((g) => {
    if (g.kind === 'money') {
      // work-first: money is tracked by the caller but never blocks.
      if (paymentMode === 'work_first') return false;
      return !state.moneyPaid[g.id];
    }
    if (g.kind === 'curation') return !state.curationCleared;
    // qa: Rohit sign-off (ancillary tiers). A quality gate — always
    // blocks until cleared (work-first relaxes money only, never QA).
    if (g.kind === 'qa') return !(state.qaSignedOff ?? {})[g.id];
    return !state.consultationDone[g.id]; // consultation
  });
  return { allowed: failed.length === 0, blockedBy: failed };
}

/**
 * The server-recheck function shape. The real implementation
 * (`makeServerGateRecheck` in conversation.ts) round-trips to the
 * `recheckGate` Cloud Function, which re-reads `projects/{pid}.gateState`
 * + tier server-side, re-runs canEnterStage and enforces curation =
 * assignee|admin. A transport failure resolves to a BLOCK (fail-safe).
 */
export type ServerGateRecheck = (
  tpl: EngagementTemplate,
  stageKey: string,
  clientVerdict: { allowed: boolean; blockedBy: Gate[] },
) => Promise<{ allowed: boolean; blockedBy: Gate[] }>;

/**
 * SERVER-RECHECK — Phase 1 Task B (2026-05-21). NO LONGER a no-op.
 *
 * Delivery-guarding gates (curation, final money gate) require an
 * authoritative server check — a client can lie. This is the single
 * typed enforcement point. The real recheck (`recheckGate` Cloud
 * Function) is passed in as `serverRecheck`:
 *
 *  - serverRecheck SUPPLIED  → its verdict is authoritative; the client
 *    verdict is never trusted for delivery. (The CF re-reads gateState +
 *    tier server-side; a transport failure already resolves to a block
 *    inside makeServerGateRecheck.)
 *  - serverRecheck MISSING   → FAIL-SAFE: a delivery-guarding write must
 *    NOT proceed on an unverified gate, so this returns `allowed:false`.
 *    The old behaviour (silently returning the client verdict) was the
 *    security gap this closes.
 *
 * Non-delivery-guarding stages never reach here — `assertStageEntry`
 * (projectLedger.ts) only invokes the recheck for `isDeliveryGuarded`
 * stages — so a missing recheck on a plain work stage is not blocked.
 */
export async function assertServerSideGate(
  tpl: EngagementTemplate,
  stageKey: string,
  clientVerdict: { allowed: boolean; blockedBy: Gate[] },
  serverRecheck?: ServerGateRecheck,
): Promise<{ allowed: boolean; blockedBy: Gate[] }> {
  if (!serverRecheck) {
    // FAIL-SAFE: no server authority available → block the delivery write.
    return { allowed: false, blockedBy: clientVerdict.blockedBy };
  }
  return serverRecheck(tpl, stageKey, clientVerdict);
}

/* ── Escalation (working-calendar resolved) ── */

/**
 * The Sprint-3 replacement for Sprint-2's `now + 24h` wall-clock.
 * Resolves the escalation anchor through the working-calendar resolver
 * so weekends/holidays/after-hours don't count. Pure: returns a Date
 * (the persistence layer mints the Firestore Timestamp, exactly as
 * Sprint 2 kept extraction pure).
 */
export function escalationAnchor(
  tpl: EngagementTemplate,
  from: Date = new Date(),
  cal: WorkingCalendar = DEFAULT_CALENDAR,
): Date {
  return addBusinessHours(from, tpl.escalationBusinessHours, cal);
}

/* ── Agreed-price gate seeding (the $40 fix) ── */

/**
 * AGREED-PRICE FIX (2026-06-15) — port of the lead-quote-payment
 * `seedGateAmounts`. Given a tier and an AGREED engagement value (in the
 * engagement's own currency units — ₹ or $), returns the per-gate expected
 * amount map that OVERRIDES the template defaults in the money module.
 *
 *  - FIXED gates (template `amountINR != null`, e.g. L1A/L1B/L3A ₹11,000, L2B
 *    ₹31,000/₹20,000): rescaled proportionally to `value / templateFixedTotal`;
 *    the LAST fixed gate absorbs the rounding remainder so the seeded amounts
 *    sum EXACTLY to `value`.
 *  - FRACTION gates (`fraction != null`, e.g. L3B 5×20%): NOT overridden — they
 *    already resolve as `round(value × fraction)` in the money module.
 *  - When `value` already equals the template fixed total (the standing INR
 *    price, un-negotiated), the map is returned EMPTY (no override needed) so
 *    nothing changes for the existing happy path.
 *
 * This is the heart of the fix: Tarveen's $40 L1A → the single advance gate is
 * seeded at $40, so the gate expects $40 (not the ₹11,000 template figure) and
 * clears in full on a $40 payment.
 */
export function seedGateAmounts(
  tier: Tier | string,
  value: number,
): Record<string, number> {
  const tpl = getEngagementTemplate(tier);
  if (!tpl) return {};
  const moneyGates = tpl.gates.filter(
    (g): g is Extract<typeof g, { kind: 'money' }> => g.kind === 'money',
  );
  const fixed = moneyGates.filter((g) => g.amountINR != null);
  // No fixed gates (purely %-split tier like L3B) → fractions handle it.
  if (fixed.length === 0) return {};
  const templateTotal = fixed.reduce((s, g) => s + (g.amountINR as number), 0);
  if (templateTotal <= 0) return {};
  // Standing INR price, un-negotiated → the template is already correct.
  if (Math.round(value) === templateTotal) return {};
  const out: Record<string, number> = {};
  let allocated = 0;
  fixed.forEach((g, i) => {
    if (i === fixed.length - 1) {
      // Last gate absorbs the remainder → exact sum to `value`.
      out[g.id] = Math.round(value) - allocated;
    } else {
      const share = Math.round(((g.amountINR as number) / templateTotal) * value);
      out[g.id] = share;
      allocated += share;
    }
  });
  return out;
}

/* ════════════════════ THE ONE CANONICAL GATE-AMOUNT RESOLVER ════════════════════ */

/**
 * THE SINGLE SOURCE OF TRUTH for a money gate's expected amount (2026-06-15).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS — kill the multi-place bug
 * ─────────────────────────────────────────────────────────────────────────
 *  Before this, the gate amount was computed independently in at least THREE
 *  places (`moneyModel.projectMoney`, `recordPayment.payableGates`, the
 *  agreed-price panel) — each re-deriving `seeded ?? amountINR ?? fraction`.
 *  When the owner set Tarveen's agreed price ($40) it reached the project-page
 *  gate + summary but NOT the Record-a-payment modal (which kept reading the
 *  template ₹11,000). Fixing one screen kept missing the others.
 *
 *  THE PRINCIPLE (owner-locked): there is ONE agreed price per engagement.
 *  That single number — in the engagement's currency — IS the money gate. The
 *  template `amountINR` (₹11,000 etc.) is ONLY the default used when no price
 *  has been agreed yet.
 *
 *  THE RULE (one place, every consumer calls it):
 *   1. An AGREED per-gate amount (`gateAmounts[gate.id]`, seeded by the Quote /
 *      agreed-price panel via `seedGateAmounts`) WINS — in the engagement
 *      currency. This is the $40.
 *   2. Else the template FIXED amount (`amountINR`) — the standing default.
 *   3. Else a %-split gate resolves as `round(value × fraction)`.
 *   4. Else 0 (unpriced).
 *
 *  PURE + total. `value` is the engagement value in its own currency units.
 *  `gateAmounts` is the project's seeded override map (empty → template).
 */
export function resolveGateAmount(
  gate: Pick<MoneyGate, 'id' | 'amountINR' | 'fraction'>,
  value: number,
  gateAmounts?: Record<string, number> | null,
): number {
  // 1. Agreed override wins (the $40 — the single source of truth).
  const seeded = gateAmounts ? gateAmounts[gate.id] : undefined;
  if (typeof seeded === 'number' && Number.isFinite(seeded)) return seeded;
  // 2. Template fixed default.
  if (gate.amountINR != null) return gate.amountINR;
  // 3. %-split against the engagement value.
  if (gate.fraction != null) return Math.round((value || 0) * gate.fraction);
  // 4. Unpriced.
  return 0;
}

/** Exposed for tests + admin tooling. */
export const __TEMPLATES = TEMPLATES;
